import { offlineDb } from '@/db/offlineDb';
import { primaryListOfflineService } from '@/services/offline/primary-list-offline.service';
import { syncStatusService, type SyncErrorDetail, type SyncStatusValue } from '@/services/sync/syncStatus.service';
import { supabase } from '@/integrations/supabase/client';
import OfflineLogger from '@/utils/offlineLogger';

export interface ValidationResult {
  isValid: boolean;
  missingEntities: string[];
  details: Record<string, number>;
  criticalMissing: string[];
  warnings: string[];
}

export interface EntityValidation {
  name: string;
  required: boolean;
  minCount?: number;
  validator: (userId: string) => Promise<{ count: number; isValid: boolean; details?: any }>;
}

class SyncValidationService {
  private entityValidations: EntityValidation[] = [
    {
      name: 'user_categories',
      required: false, // ✅ FIX: Non obbligatorio per utenti nuovi
      minCount: 0,
      validator: this.validateCategories.bind(this)
    },
    {
      name: 'user_products',
      required: false, // ✅ FIX: Non obbligatorio per utenti nuovi
      minCount: 0,
      validator: this.validateProducts.bind(this)
    },
    {
      name: 'shopping_lists',
      required: true,
      minCount: 1, // Almeno la lista primaria
      validator: this.validateShoppingLists.bind(this)
    },
    {
      name: 'primary_list',
      required: true,
      minCount: 1,
      validator: this.validatePrimaryList.bind(this)
    },
    {
      name: 'user_shops',
      required: false,
      validator: this.validateShops.bind(this)
    },
    {
      name: 'recurring_products',
      required: false,
      validator: this.validateRecurringProducts.bind(this)
    },
    {
      name: 'images',
      required: false,
      validator: this.validateImages.bind(this)
    }
  ];

  async performComprehensiveValidation(
    userId: string | null,
    expectEmpty = false
  ): Promise<ValidationResult> {
    const startTime = Date.now();
    const missingEntities: string[] = [];
    const criticalMissing: string[] = [];
    const warnings: string[] = [];
    const details: Record<string, number> = {};

    const idDisplay = userId ? userId.substring(0, 8) : 'unknown';
    OfflineLogger.info(
      'SyncValidationService',
      `Starting comprehensive validation for user ${idDisplay}${expectEmpty ? ' (expect empty)' : ''}`
    );

    for (const entityValidation of this.entityValidations) {
      const skipRequiredCheck =
        expectEmpty && ['shopping_lists', 'primary_list'].includes(entityValidation.name);

      try {
        const result = await entityValidation.validator(userId);
        details[entityValidation.name] = result.count;

        // Controlla se l'entità è valida
        const isCountValid = !entityValidation.minCount || result.count >= entityValidation.minCount;
        const isValid = result.isValid && isCountValid;

        if (!isValid) {
          missingEntities.push(entityValidation.name);

          if (entityValidation.required && !skipRequiredCheck) {
            criticalMissing.push(entityValidation.name);
            if (expectEmpty) {
              OfflineLogger.warn(
                'SyncValidationService',
                `Missing required entity ${entityValidation.name} (expected empty cache)`
              );
            } else {
              OfflineLogger.error(
                'SyncValidationService',
                `CRITICAL: Missing required entity ${entityValidation.name}. Count: ${result.count}, Required: ${entityValidation.minCount || 1}`
              );
            }
          } else {
            if (expectEmpty) {
              OfflineLogger.info(
                'SyncValidationService',
                `ℹ️ ${entityValidation.name} missing - ok if absent`
              );
            } else {
              warnings.push(`Optional entity ${entityValidation.name} not found or incomplete`);
              OfflineLogger.warn(
                'SyncValidationService',
                `INFO: Optional entity ${entityValidation.name} missing. Count: ${result.count} (this is normal for new users)`
              );
            }
          }
        } else {
          OfflineLogger.info(
            'SyncValidationService',
            `✅ ${entityValidation.name}: ${result.count} records - VALID`
          );
        }
      } catch (error) {
        const message = `Error validating ${entityValidation.name}: ${error}`;
        if (expectEmpty) {
          if (skipRequiredCheck) {
            OfflineLogger.info('SyncValidationService', message);
          } else {
            OfflineLogger.warn('SyncValidationService', message);
          }
        } else {
          OfflineLogger.error('SyncValidationService', message);
        }
        missingEntities.push(entityValidation.name);
        if (entityValidation.required && !skipRequiredCheck) {
          criticalMissing.push(entityValidation.name);
        }
      }
    }

    const duration = Date.now() - startTime;
    const isValid = criticalMissing.length === 0;

    const result: ValidationResult = {
      isValid,
      missingEntities,
      details,
      criticalMissing,
      warnings
    };

    OfflineLogger.info('SyncValidationService',
      `Validation completed in ${duration}ms. Valid: ${isValid}. Critical missing: ${criticalMissing.length}. Details: ${JSON.stringify(details)}`
    );

    const { data } = await supabase.auth.getSession();
    const shouldPersistRemote =
      !!userId &&
      !expectEmpty &&
      data.session?.user?.id === userId;

    if (!shouldPersistRemote) {
      const reason = !userId
        ? 'utente non definito'
        : expectEmpty
          ? 'validazione eseguita in modalità expectEmpty'
          : 'sessione corrente assente o non corrispondente all\'utente richiesto';
      OfflineLogger.info(
        'SyncValidationService',
        `Remote validation persistence skipped: ${reason}`
      );
    }

    // Salva risultato validazione per debugging
    await this.storeValidationResult(userId, result, { persistRemote: shouldPersistRemote });

    return result;
  }

  private async validateCategories(userId: string): Promise<{ count: number; isValid: boolean }> {
    const count = await offlineDb.categories
      .where('user_id')
      .equals(userId)
      .and(cat => cat._syncStatus !== 'deleted')
      .count();
    
    return { count, isValid: true }; // ✅ FIX: Sempre valido, anche con 0 categorie
  }

  private async validateProducts(userId: string): Promise<{ count: number; isValid: boolean }> {
    const count = await offlineDb.products
      .where('user_id')
      .equals(userId)
      .and(product => product._syncStatus !== 'deleted')
      .count();
    
    return { count, isValid: true }; // ✅ FIX: Sempre valido, anche con 0 prodotti
  }

  private async validateShoppingLists(userId: string): Promise<{ count: number; isValid: boolean }> {
    const count = await offlineDb.shoppingLists
      .where('user_id')
      .equals(userId)
      .and(list => list._syncStatus !== 'deleted')
      .count();
    
    return { count, isValid: count > 0 };
  }

  private async validatePrimaryList(userId: string): Promise<{ count: number; isValid: boolean }> {
    // ✅ FIX: Prova diversi metodi per trovare la lista primaria
    try {
      // Metodo 1: usa il servizio dedicato
      const primaryListService = await primaryListOfflineService.getPrimaryListOffline(userId);
      if (primaryListService) {
        OfflineLogger.info('SyncValidationService', `Primary list found via service: ${primaryListService.id}`);
        return { count: 1, isValid: true };
      }

      // ✅ FIX: Metodo 2: cerca tutte le liste e filtra manualmente per evitare problemi con Dexie
      const allLists = await offlineDb.shoppingLists
        .where('user_id')
        .equals(userId)
        .and(list => list._syncStatus !== 'deleted')
        .toArray();

      // ✅ FIX: Controllo manuale per is_primary con gestione corretta dei tipi
      const primaryList = allLists.find(list => {
        // Gestisce sia boolean che number (1/0) per is_primary
        const isPrimary = list.is_primary;
        if (typeof isPrimary === 'boolean') {
          return isPrimary === true;
        }
        if (typeof isPrimary === 'number') {
          return isPrimary === 1;
        }
        if (typeof isPrimary === 'string') {
          return isPrimary === 'true' || isPrimary === '1';
        }
        return false;
      });

      if (primaryList) {
        OfflineLogger.info('SyncValidationService', `Primary list found manually: ${primaryList.id} (is_primary: ${primaryList.is_primary})`);
        return { count: 1, isValid: true };
      }

      OfflineLogger.warn('SyncValidationService', `No primary list found. Total lists: ${allLists.length}`);
      return { count: 0, isValid: false };

    } catch (error) {
      OfflineLogger.error('SyncValidationService', `Error validating primary list: ${error}`);
      return { count: 0, isValid: false };
    }
  }

  private async validateShops(userId: string): Promise<{ count: number; isValid: boolean }> {
    const count = await offlineDb.shops
      .where('user_id')
      .equals(userId)
      .and(shop => shop._syncStatus !== 'deleted')
      .count();
    
    return { count, isValid: true };
  }

  private async validateRecurringProducts(userId: string): Promise<{ count: number; isValid: boolean }> {
    const count = await offlineDb.recurringProducts
      .where('user_id')
      .equals(userId)
      .and(product => product._syncStatus !== 'deleted')
      .count();

    return { count, isValid: true };
  }

  private async validateImages(userId: string): Promise<{ count: number; isValid: boolean }> {
    const count = await offlineDb.imageCache.count();
    return { count, isValid: true };
  }

  private async storeValidationResult(
    userId: string | null,
    result: ValidationResult,
    options?: { persistRemote: boolean }
  ): Promise<void> {
    try {
      const validationData = {
        userId,
        timestamp: Date.now(),
        ...result
      };
      const key = `validation_${userId ?? 'unknown'}`;
      localStorage.setItem(key, JSON.stringify(validationData));

      const persistRemote = options?.persistRemote ?? true;

      if (!userId) {
        return;
      }

      if (!persistRemote) {
        OfflineLogger.info(
          'SyncValidationService',
          'Skipping remote validation persistence due to persistRemote=false'
        );
        return;
      }

      await this.persistRemoteValidation(userId, result);
    } catch (error) {
      OfflineLogger.error('SyncValidationService.storeValidationResult', error);
    }
  }

  private async persistRemoteValidation(userId: string, result: ValidationResult) {
    try {
      const timestamp = new Date();

      const operations = this.entityValidations.map(async (entityValidation) => {
        const entityName = entityValidation.name;
        const isMissing = result.missingEntities.includes(entityName);
        const isCritical = result.criticalMissing.includes(entityName);

        const status: SyncStatusValue = isMissing ? (isCritical ? 'error' : 'warning') : 'success';
        const errorDetails: SyncErrorDetail[] = [];

        if (isMissing) {
          const message = isCritical
            ? `Entità obbligatoria mancante: ${entityName}`
            : `Entità opzionale incompleta: ${entityName}`;

          const detail: SyncErrorDetail = {
            message,
            code: isCritical ? 'validation_missing_required' : 'validation_missing_optional',
            metadata: {
              required: entityValidation.required,
              minCount: entityValidation.minCount ?? null,
              observedCount: result.details?.[entityName] ?? 0,
            }
          };

          errorDetails.push(detail);

          await syncStatusService.appendSyncError({
            userId,
            entity: entityName,
            error: detail,
            status,
          });
        }

        await syncStatusService.upsertSyncStatus({
          userId,
          entity: entityName,
          status,
          lastSyncedAt: timestamp,
          errorDetails,
        });
      });

      await Promise.all(operations);

      if (result.warnings.length > 0) {
        const warningsDetail: SyncErrorDetail = {
          message: 'Avvisi di validazione sincronizzazione',
          code: 'validation_warnings',
          metadata: { warnings: result.warnings },
        };

        await syncStatusService.upsertSyncStatus({
          userId,
          entity: 'sync_validation',
          status: 'warning',
          lastSyncedAt: timestamp,
          errorDetails: [warningsDetail],
        });

        await syncStatusService.appendSyncError({
          userId,
          entity: 'sync_validation',
          status: 'warning',
          error: warningsDetail,
        });
      } else {
        await syncStatusService.upsertSyncStatus({
          userId,
          entity: 'sync_validation',
          status: 'success',
          lastSyncedAt: timestamp,
          errorDetails: [],
        });
      }
    } catch (error) {
      OfflineLogger.error('SyncValidationService.persistRemoteValidation', error);
    }
  }

  async getLastValidationResult(userId: string | null): Promise<ValidationResult | null> {
    try {
      const key = `validation_${userId ?? 'unknown'}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const data = JSON.parse(stored);
        return {
          isValid: data.isValid,
          missingEntities: data.missingEntities,
          details: data.details,
          criticalMissing: data.criticalMissing,
          warnings: data.warnings
        };
      }
    } catch (error) {
      OfflineLogger.error('SyncValidationService.getLastValidationResult', error);
    }
    return null;
  }

  async validateEntityIntegrity(entityName: string, userId: string): Promise<boolean> {
    const entityValidation = this.entityValidations.find(v => v.name === entityName);
    if (!entityValidation) {
      OfflineLogger.warn('SyncValidationService', `Unknown entity: ${entityName}`);
      return false;
    }

    try {
      const result = await entityValidation.validator(userId);
      const isValid = result.isValid && 
        (!entityValidation.minCount || result.count >= entityValidation.minCount);
      
      OfflineLogger.info('SyncValidationService', 
        `Entity ${entityName} validation: ${isValid ? 'VALID' : 'INVALID'} (count: ${result.count})`
      );
      
      return isValid;
    } catch (error) {
      OfflineLogger.error('SyncValidationService', `Error validating entity ${entityName}: ${error}`);
      return false;
    }
  }
}

export const syncValidationService = new SyncValidationService();
